/**
 * Custom Card Component
 * Healthcare-styled card with header, body, and footer sections
 */

import { Card as AntCard, CardProps as AntCardProps } from 'antd';
import classNames from 'classnames';
import './CustomCard.scss';
import { ReactNode } from 'react';

export interface CustomCardProps extends Omit<AntCardProps, 'type'> {
  /** Card header content */
  header?: ReactNode;
  /** Card footer content */
  footer?: ReactNode;
  /** Enable hover effect */
  hoverable?: boolean;
  /** Card variant */
  cardVariant?: 'default' | 'bordered' | 'shadowed';
  /** Show loading state */
  isLoading?: boolean;
}

/**
 * CustomCard Component
 * 
 * @example
 * ```tsx
 * <CustomCard
 *   header={<h3>Patient Information</h3>}
 *   footer={<CustomButton>View Details</CustomButton>}
 *   hoverable
 * >
 *   <p>Card content goes here</p>
 * </CustomCard>
 * ```
 */
export const CustomCard: React.FC<CustomCardProps> = ({
  header,
  footer,
  hoverable = false,
  cardVariant = 'default',
  isLoading = false,
  className,
  children,
  ...rest
}) => {
  const cardClasses = classNames(
    'custom-card',
    `custom-card--${cardVariant}`,
    {
      'custom-card--hoverable': hoverable,
      'custom-card--loading': isLoading,
    },
    className
  );

  return (
    <AntCard
      {...rest}
      className={cardClasses}
      loading={isLoading}
      bordered={cardVariant === 'bordered'}
    >
      {header && <div className="custom-card__header">{header}</div>}
      
      <div className="custom-card__body">{children}</div>
      
      {footer && <div className="custom-card__footer">{footer}</div>}
    </AntCard>
  );
};

export default CustomCard;

